import { t } from "@galaxy-io/dls/theme/tokens/t";
import Widget from "@galaxy-io/dls/widget/Widget";
import { styled } from "@linaria/react";
import type { ReactNode } from "react";

/**
 * Fixed height, so moving between steps never resizes the dialog. A flush body
 * leaves scrolling to its own content, such as the recent imports table.
 */
export const ImportBody = styled.div<{ $flush?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $flush }) => ($flush ? "0" : "24px")};
  padding: ${({ $flush }) => ($flush ? "0" : "16px")};
  height: min(600px, calc(100dvh - 160px));
  min-height: 0;
  overflow: ${({ $flush }) => ($flush ? "hidden" : "auto")};
`;
export const DropArea = styled.div<{ $grow: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${t.space[12]};
  padding: ${t.space[16]};
  min-height: 0;
  flex: ${({ $grow }) => ($grow ? "1 1 auto" : "0 0 auto")};
  border-bottom: ${({ $grow }) => ($grow ? "none" : `0.5px solid ${t.color.border.primary}`)};
`;
export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${t.space[12]};
  min-width: 0;
`;

export const SectionHeading = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${t.space[12]};
  > :first-child {
    display: flex;
    flex-direction: column;
    gap: ${t.space[4]};
    min-width: 0;
  }
`;
/** A bordered list whose rows are hairline-separated, as in record drawers. */
const ListRows = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  > :not(:last-child) {
    border-bottom: 0.5px solid ${t.color.border.primary};
  }
`;
export const FileCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${t.space[8]};
  min-width: 0;
  > :first-child {
    flex-shrink: 0;
  }
`;
export const Stacked = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${t.space[2]};
  min-width: 0;
`;
const FileBarLayout = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: ${t.space[12]};
  padding: ${t.space[12]};
`;
export const MappingRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 220px minmax(0, 0.8fr);
  align-items: center;
  gap: ${t.space[16]};
  padding: ${t.space[8]} ${t.space[12]};
  min-height: 52px;
  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    > :last-child {
      display: none;
    }
  }
`;
export const ReviewRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${t.space[12]};
  padding: ${t.space[12]};
  min-width: 0;
`;
export const ReviewLine = styled.div`
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) auto auto;
  align-items: center;
  gap: ${t.space[12]};
`;
export const Outcomes = styled.div`
  display: flex;
  align-items: center;
  gap: ${t.space[12]};
  flex-wrap: wrap;
  justify-content: flex-end;
  min-width: 0;
  > div {
    display: inline-flex;
    align-items: center;
    gap: ${t.space[4]};
    min-width: 0;
    max-width: 180px;
  }
  @media (max-width: 640px) {
    > div {
      display: none;
    }
  }
`;
/** Indented under the row's name so issues read as part of that row. */
export const RowDetail = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${t.space[8]};
  padding-left: ${t.space[32]};
  min-width: 0;
  > div[role="list"] {
    display: flex;
    flex-direction: column;
    gap: ${t.space[4]};
  }
`;
export const Issue = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${t.space[4]};
  > :first-child {
    flex-shrink: 0;
    padding-top: ${t.space[2]};
  }
`;
export const Matches = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${t.space[12]};
`;
export const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${t.space[8]};
`;
export const ImportFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${t.space[8]};
  width: 100%;
  > div {
    display: flex;
    align-items: center;
    gap: ${t.space[8]};
  }
`;

export function List({ children }: { children: ReactNode }) {
  return (
    <Widget isFlush>
      <ListRows>{children}</ListRows>
    </Widget>
  );
}
export function FileBar({ children }: { children: ReactNode }) {
  return (
    <Widget isFlush>
      <FileBarLayout>{children}</FileBarLayout>
    </Widget>
  );
}
